import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Calendar, X, Check } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface ReferenceDocListItem {
  id: string;
  title: string;
  tags: Tag[];
  tag_count: number;
  created_at: string;
  updated_at: string;
}

export function ReferenceDocsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [docs, setDocs] = useState<ReferenceDocListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  async function loadDocs() {
    try {
      setDocs(await apiGet<ReferenceDocListItem[]>('/reference-docs'));
    } catch (err) {
      console.error('Failed to load reference docs:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDocs(); }, []);

  async function handleCreate(title: string, tagIds: string[]) {
    try {
      const doc = await apiPost<ReferenceDocListItem>('/reference-docs', {
        title,
        tag_ids: tagIds,
      });
      setShowCreate(false);
      toast.success('Reference doc created');
      navigate(`/references/${doc.id}`);
    } catch (err) {
      console.error('Failed to create reference doc:', err);
      toast.error('Failed to create reference doc');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this reference document?')) return;
    try {
      await apiDelete(`/reference-docs/${id}`);
      toast.success('Reference doc deleted');
      loadDocs();
    } catch (err) {
      console.error('Failed to delete reference doc:', err);
      toast.error('Failed to delete reference doc');
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Reference Documents</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          <Plus size={16} /> New Reference Doc
        </button>
      </div>

      {docs.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <FileText size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No reference documents yet</p>
          <p className="text-sm">Create a reference doc to aggregate tagged content from your meetings.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors cursor-pointer group"
              onClick={() => navigate(`/references/${doc.id}`)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-accent flex-shrink-0" />
                  <span className="text-sm font-medium text-text-primary truncate">{doc.title}</span>
                </div>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {doc.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="text-xs px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: tag.color + '20', color: tag.color }}
                    >
                      {tag.name}
                    </span>
                  ))}
                  {doc.tags.length === 0 && (
                    <span className="text-xs text-text-muted italic">No tag filters</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 ml-4">
                <span className="text-xs text-text-muted flex items-center gap-1">
                  <Calendar size={12} />
                  {formatDate(doc.updated_at)}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }}
                  className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-danger transition-all"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateReferenceDocModal
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
    </div>
  );
}

function CreateReferenceDocModal({
  onSave,
  onClose,
}: {
  onSave: (title: string, tagIds: string[]) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [loadingTags, setLoadingTags] = useState(true);

  useEffect(() => {
    apiGet<Tag[]>('/tags')
      .then(setAllTags)
      .catch(console.error)
      .finally(() => setLoadingTags(false));
  }, []);

  function toggleTag(tagId: string) {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">New Reference Document</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              placeholder="e.g., Infrastructure Overview"
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-2">Filter by Tags</label>
            <p className="text-xs text-text-muted mb-2">
              Select tags to aggregate tagged content from all meetings.
            </p>
            {loadingTags ? (
              <div className="text-xs text-text-muted">Loading tags...</div>
            ) : allTags.length === 0 ? (
              <div className="text-xs text-text-muted">No tags available. Create tags first.</div>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
                {allTags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => toggleTag(tag.id)}
                    className={`flex items-center gap-2 w-full px-3 py-1.5 rounded-md text-left text-sm transition-colors ${
                      selectedTagIds.has(tag.id)
                        ? 'bg-accent/10 text-text-primary'
                        : 'text-text-secondary hover:bg-bg-hover'
                    }`}
                  >
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="flex-1">{tag.name}</span>
                    {selectedTagIds.has(tag.id) && (
                      <Check size={14} className="text-accent" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
          <button
            onClick={() => title.trim() && onSave(title.trim(), Array.from(selectedTagIds))}
            disabled={!title.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
